/**
 * Workflow Store - Combined Zustand store for the Workflow Editor
 *
 * Uses the slice pattern for modular state management.
 * Based on React Flow best practices and Zustand patterns.
 *
 * @see https://reactflow.dev/learn/advanced-use/state-management
 * @see https://zustand.docs.pmnd.rs/guides/slices-pattern
 */

import { create } from 'zustand';
import { devtools, subscribeWithSelector } from 'zustand/middleware';
import { useShallow } from 'zustand/react/shallow';
import type { WorkflowStore, WorkflowNode, WorkflowDefinition } from './types';
import { createWorkspaceSlice } from './workspaceSlice';
import { createSelectionSlice } from './selectionSlice';
import { createPaletteSlice } from './paletteSlice';
import { createPersistenceSlice } from './persistenceSlice';
import { createValidationSlice } from './validationSlice';

/**
 * Combined workflow store with all slices
 */
export const useWorkflowStore = create<WorkflowStore>()(
  devtools(
    subscribeWithSelector((...args) => ({
      // Combine all slices
      ...createWorkspaceSlice(...args),
      ...createSelectionSlice(...args),
      ...createPaletteSlice(...args),
      ...createPersistenceSlice(...args),
      ...createValidationSlice(...args),

      // Computed state helpers
      getSelectedNode: () => {
        const state = args[1]() as WorkflowStore;
        if (!state.selectedNodeId) return undefined;
        return state.nodes.find((n) => n.id === state.selectedNodeId);
      },

      getNodeById: (nodeId: string) => {
        const state = args[1]() as WorkflowStore;
        return state.nodes.find((n) => n.id === nodeId);
      },

      getConnectedNodes: (nodeId: string): WorkflowNode[] => {
        const state = args[1]() as WorkflowStore;
        const connectedIds = new Set<string>();

        // Find all edges connected to this node
        state.edges.forEach((edge) => {
          if (edge.source === nodeId) connectedIds.add(edge.target);
          if (edge.target === nodeId) connectedIds.add(edge.source);
        });

        return state.nodes.filter((n) => connectedIds.has(n.id));
      },

      exportWorkflow: (): WorkflowDefinition => {
        const state = args[1]() as WorkflowStore;
        return {
          version: '1.0',
          metadata: {
            name: state.currentWorkflowName,
            description: state.currentWorkflowDescription,
            createdAt: state.lastSavedAt || new Date().toISOString(),
            updatedAt: new Date().toISOString(),
            // DISPLAY MESSAGES ONLY - all prompts are in nodes
            welcomeMessage: state.workflowMetadata.welcomeMessage || undefined,
            errorMessage: state.workflowMetadata.errorMessage || undefined
          },
          nodes: state.nodes,
          edges: state.edges,
          viewport: state.viewport
        };
      }
    })),
    {
      name: 'workflow-store',
      enabled: process.env.NODE_ENV === 'development'
    }
  )
);

// Re-export types for convenience
export type {
  WorkflowStore,
  WorkflowNode,
  WorkflowEdge,
  WorkflowNodeData,
  WorkflowDefinition,
  WorkflowMetadata,
  Workflow,
  // Node data types
  StartNodeData,
  EndNodeData,
  SystemNodeData,
  ConditionNodeData,
  OrchestratorNodeData,
  AgentNodeData,
  ToolNodeData,
  // Type aliases
  AgentType,
  SystemNodeType,
  OrchestratorNodeType,
  // Validation
  ValidationError,
  // Palette
  NodePaletteItem
} from './types';

// Re-export type guards for node type checking
export {
  isOrchestratorNode,
  isAgentNode,
  isSystemNode,
  isConditionNode,
  hasEditablePrompt
} from './types';

// ===== Selective Hooks for Performance =====

/**
 * Hook for React Flow handlers
 * useShallow prevents infinite re-renders from new object references
 */
export const useWorkflowHandlers = () =>
  useWorkflowStore(
    useShallow((state) => ({
      onNodesChange: state.onNodesChange,
      onEdgesChange: state.onEdgesChange,
      onConnect: state.onConnect
    }))
  );

/**
 * Hook for selection state
 */
export const useWorkflowSelection = () =>
  useWorkflowStore(
    useShallow((state) => ({
      selectedNodeId: state.selectedNodeId,
      selectedEdgeId: state.selectedEdgeId,
      multiSelectedNodeIds: state.multiSelectedNodeIds,
      setSelectedNodeId: state.setSelectedNodeId,
      setSelectedEdgeId: state.setSelectedEdgeId,
      clearSelection: state.clearSelection,
      getSelectedNode: state.getSelectedNode
    }))
  );

/**
 * Hook for palette state
 */
export const useWorkflowPalette = () =>
  useWorkflowStore(
    useShallow((state) => ({
      isPaletteOpen: state.isPaletteOpen,
      isDragging: state.isDragging,
      draggedNodeType: state.draggedNodeType,
      setIsPaletteOpen: state.setIsPaletteOpen,
      togglePalette: state.togglePalette,
      setDraggedNodeType: state.setDraggedNodeType
    }))
  );

/**
 * Hook for persistence state
 */
export const useWorkflowPersistence = () =>
  useWorkflowStore(
    useShallow((state) => ({
      currentWorkflowId: state.currentWorkflowId,
      currentWorkflowName: state.currentWorkflowName,
      currentWorkflowDescription: state.currentWorkflowDescription,
      isDirty: state.isDirty,
      isSaving: state.isSaving,
      lastSavedAt: state.lastSavedAt,
      setCurrentWorkflowId: state.setCurrentWorkflowId,
      setCurrentWorkflowName: state.setCurrentWorkflowName,
      setCurrentWorkflowDescription: state.setCurrentWorkflowDescription,
      markAsSaved: state.markAsSaved,
      setIsSaving: state.setIsSaving,
      exportWorkflow: state.exportWorkflow
    }))
  );

/**
 * Hook for workflow metadata (DISPLAY MESSAGES ONLY - prompts are in nodes)
 */
export const useWorkflowMetadata = () =>
  useWorkflowStore(
    useShallow((state) => ({
      workflowMetadata: state.workflowMetadata,
      setWorkflowMetadata: state.setWorkflowMetadata,
      setWelcomeMessage: state.setWelcomeMessage,
      setErrorMessage: state.setErrorMessage
    }))
  );

/**
 * Hook for validation state
 */
export const useWorkflowValidation = () =>
  useWorkflowStore(
    useShallow((state) => ({
      validationErrors: state.validationErrors,
      isValid: state.isValid,
      validateWorkflow: state.validateWorkflow,
      clearValidationErrors: state.clearValidationErrors
    }))
  );

/**
 * Hook for workflow operations (load, clear, export)
 */
export const useWorkflowOperations = () =>
  useWorkflowStore(
    useShallow((state) => ({
      loadWorkflow: state.loadWorkflow,
      clearWorkspace: state.clearWorkspace,
      exportWorkflow: state.exportWorkflow,
      validateWorkflow: state.validateWorkflow
    }))
  );
